import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';

import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import Clear from '@mui/icons-material/Clear';
import Save from '@mui/icons-material/Save';

import { Slots } from '../Slots/Slots';

const isNewShift = (id) => /^new-\d+(-\d+)?$/.test(id);

export const ShiftAddDialog = ({ open, shift, roles, onClose, onSave }) => {
    const [ editShift, setEditShift ] = useState(null);
    const [ canSave, setCanSave ] = useState(false);

    useEffect(() => {
        if (open && shift) {
            setEditShift(shift);
        }
    }, [ open, shift ]);

    useEffect(() => {
        setCanSave(!!editShift?.name && !!editShift?.date && !!editShift?.start && !!editShift?.end);
    }, [ editShift ]);

    const formattedTime = (time) => {
        if (!time) {
            return '';
        }

        return /^\d{2}:\d{2}(:\d{2})?$/.test(time)
            ? DateTime.fromFormat(time, "HH:mm").toFormat("HH:mm")
            : DateTime.fromMillis(time).toFormat("HH:mm")
    };

    const isValidTimeString = (timeString) => {
        // Validate timeString (HH:mm)
        return /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeString);
    };

    const isValidDateString = (dateString) => {
        // Validate dateString (YYYY-MM-DD)
        return /^\d{4}-\d{2}-\d{2}$/.test(dateString) && !isNaN(DateTime.fromISO(dateString).toMillis());
    };

    const convertTimeToEpoch = (timeString, dateString) => {
        if (!isValidTimeString(timeString) || !isValidDateString(dateString)) {
            return null;
        }

        const [ hours, minutes ] = timeString.split(':').map(Number);

        return DateTime.fromObject(
            {
                year: Number(dateString.slice(0, 4)),
                month: Number(dateString.slice(5, 7)),
                day: Number(dateString.slice(8, 10)),
                hour: hours,
                minute: minutes,
            },
            { zone: 'local' }
        ).toMillis();
    };

    const handleShiftChange = (e) => {
        const { name, value } = e.target;
        setEditShift(prev => ({ ...prev, [ name ]: value }));
    };

    const handleDateChange = (newValue) => {
        const dateValue = newValue ? newValue.toISODate() : '';
        setEditShift(prev => ({ ...prev, date: dateValue }));
    };

    const handleSlotChange = (slots) => {
        setEditShift(prev => ({ ...prev, slots }));
    };

    const handleSave = () => {
        const startEpoch = convertTimeToEpoch(formattedTime(editShift.start), editShift.date);
        const endEpoch = convertTimeToEpoch(formattedTime(editShift.end), editShift.date);

        onSave({
            ...editShift,
            start: startEpoch !== null ? startEpoch : editShift.start,
            end: endEpoch !== null ? endEpoch : editShift.end,
        });

        onClose();
    };

    const handleCancel = () => {
        setEditShift(null);
        onClose();
    };

    if (!editShift) return null;

    return (
        <Dialog
            open={ open }
            onClose={ handleCancel }
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Create New Shift</DialogTitle>
            <DialogContent>
                <Box sx={ { pt: 2 } }>
                    <Grid container spacing={ 2 }>
                        <Grid size={ 4 }>
                            <TextField
                                fullWidth
                                id={ `shiftName-${ editShift.id }` }
                                label="Name"
                                name="name"
                                value={ editShift.name || '' }
                                onChange={ handleShiftChange } />
                        </Grid>

                        <Grid size={ 2 }>
                            <LocalizationProvider dateAdapter={ AdapterLuxon }>
                                <DatePicker
                                    sx={ { width: '100%' } }
                                    label="Date"
                                    id={ `shiftDate-${ editShift.id }` }
                                    name="date"
                                    format="MM/dd/yyyy"
                                    value={ editShift.date ? DateTime.fromISO(editShift.date) : null }
                                    onChange={ handleDateChange } />
                            </LocalizationProvider>
                        </Grid>

                        <Grid size={ 2 }>
                            <LocalizationProvider dateAdapter={ AdapterLuxon }>
                                <TimePicker
                                    label="Start"
                                    id={ `shiftStartTime-${ editShift.id }` }
                                    name="start"
                                    format="HH:mm"
                                    value={ editShift.start ? (typeof editShift.start === 'string' ? DateTime.fromFormat(editShift.start, "HH:mm") : DateTime.fromMillis(editShift.start)) : null }
                                    onChange={ (newValue) => {
                                        const timeValue = newValue ? newValue.toFormat("HH:mm") : '';
                                        handleShiftChange({ target: { name: 'start', value: timeValue } });
                                    } } />
                            </LocalizationProvider>
                        </Grid>

                        <Grid size={ 2 }>
                            <LocalizationProvider dateAdapter={ AdapterLuxon }>
                                <TimePicker
                                    label="End"
                                    id={ `shiftEndTime-${ editShift.id }` }
                                    name="end"
                                    format="HH:mm"
                                    value={ editShift.end ? (typeof editShift.end === 'string' ? DateTime.fromFormat(editShift.end, "HH:mm") : DateTime.fromMillis(editShift.end)) : null }
                                    onChange={ (newValue) => {
                                        const timeValue = newValue ? newValue.toFormat("HH:mm") : '';
                                        handleShiftChange({ target: { name: 'end', value: timeValue } });
                                    } } />
                            </LocalizationProvider>
                        </Grid>

                        <Grid size={ 2 }>
                            <FormControl fullWidth>
                                <InputLabel id={ `recurrence-${ editShift.id }-label` }>Repeat</InputLabel>

                                <Select
                                    labelId={ `recurrence-${ editShift.id }-label` }
                                    id={ `recurrence-${ editShift.id }` }
                                    name="recurrence"
                                    value={ editShift.recurrence || '' }
                                    onChange={ handleShiftChange }
                                    label="Repeat"
                                    MenuProps={ { disableScrollLock: true } }
                                    disabled={ isNewShift(editShift.id) ? false : !!editShift.recurrence }
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="weekly">Weekly</MenuItem>
                                    <MenuItem value="monthly">Monthly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider flexItem sx={ { mt: 3, mb: 2 } } />

                    <Slots shift={ editShift } roles={ roles } onChange={ handleSlotChange } />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={ handleCancel } startIcon={ <Clear /> }>Cancel</Button>
                <Button variant="contained" onClick={ handleSave } disabled={ !canSave } startIcon={ <Save /> }>Save Shift</Button>
            </DialogActions>
        </Dialog>
    );
};
